import type { DBSignalAgent } from "@/lib/db-types";
import { osloDateString } from "@/lib/oslo-time";

import { hasSegmentFilter } from "@/lib/lead-segment";

export const AGENT_MIN_INSTRUCTION_LENGTH = 20;
/** Kilder en segmentagent kan lese (Apify brukes ikke mot ukjente selskaper). */
export const SEGMENT_SOURCES: readonly string[] = ["website", "brreg"];

export type SignalAgentInput = Pick<
  DBSignalAgent,
  | "name"
  | "description"
  | "scope_type"
  | "customer_ids"
  | "segment"
  | "sources"
  | "instruction"
  | "signal_type"
  | "contact_reason"
  | "default_strength"
  | "frequency"
  | "is_active"
  | "cost_cap_nok_month"
>;

/** Første feil i en agentdefinisjon som norsk tekst, eller null når den er gyldig. */
export function validateSignalAgentInput(input: Partial<SignalAgentInput>): string | null {
  if (input.name !== undefined && !input.name.trim()) return "Navn mangler";
  if (input.instruction !== undefined && input.instruction.trim().length < AGENT_MIN_INSTRUCTION_LENGTH) {
    return `Instruksen må være minst ${AGENT_MIN_INSTRUCTION_LENGTH} tegn`;
  }
  if (input.sources !== undefined && input.sources.length === 0) return "Velg minst én kilde";
  if (input.contact_reason !== undefined && !input.contact_reason.trim()) return "Kontaktårsak mangler";
  if (input.scope_type === "customers" && (input.customer_ids?.length ?? 0) === 0) {
    return "Velg minst én kunde";
  }
  if (input.scope_type === "segment") {
    if (!hasSegmentFilter(input.segment)) return "Velg minst en bransje, et fylke eller en kommune for segmentet";
    if (input.sources !== undefined && input.sources.some((s) => !SEGMENT_SOURCES.includes(s))) {
      return "Segmentagenter leser bare nettsiden og Brønnøysund";
    }
  }
  if (input.cost_cap_nok_month != null && input.cost_cap_nok_month < 0) {
    return "Kostnadstaket kan ikke være negativt";
  }
  return null;
}

/** Minst så mange utfall (tatt + «ikke for meg») før en agent kan flagges. */
export const LOW_VALUE_MIN_OUTCOMES = 10;
/** Andel «ikke for meg» over denne grensen gir «Lite nyttig». */
export const LOW_VALUE_DISMISS_SHARE = 0.8;

type AgentCounters = Pick<DBSignalAgent, "dismiss_count" | "claim_count">;

/** «Ikke for meg»-andel av utfallene, eller null uten utfall. */
export function dismissShare(agent: AgentCounters): number | null {
  const total = agent.dismiss_count + agent.claim_count;
  return total === 0 ? null : agent.dismiss_count / total;
}

/** Flagges som «Lite nyttig»: over 80 % «ikke for meg» med minst 10 utfall. */
export function isLowValueAgent(agent: AgentCounters): boolean {
  const total = agent.dismiss_count + agent.claim_count;
  const share = dismissShare(agent);
  return total >= LOW_VALUE_MIN_OUTCOMES && share !== null && share > LOW_VALUE_DISMISS_SHARE;
}

/** Måneden som 'YYYY-MM' i Oslo-tid; samme nøkkel som signal_agents.cost_month. */
export function costMonthKey(now: Date): string {
  return osloDateString(now).slice(0, 7);
}

/**
 * Er kostnadstaket nådd denne måneden? Taket er agentens eget, ellers
 * systemstandarden (NULL betyr standard, ikke «ingen grense»). Kostnad fra en
 * tidligere måned teller ikke.
 */
export function isCostCapReached(
  agent: Pick<DBSignalAgent, "cost_cap_nok_month" | "cost_month_nok" | "cost_month">,
  defaultCapNok: number,
  now: Date,
): boolean {
  const cap = agent.cost_cap_nok_month ?? defaultCapNok;
  const spent = agent.cost_month === costMonthKey(now) ? agent.cost_month_nok : 0;
  return spent >= cap;
}

/**
 * Kundene en prøvekjøring bruker (maks 3). Utvalgte kunder: valgte kunder med
 * en aktiv kilde først (de andre gir ingenting å hente). Alle overvåkede:
 * kunder med en aktiv kilde agenten leser først, så resten av de overvåkede.
 */
export function pickDryRunCustomers(
  agent: Pick<SignalAgentInput, "scope_type" | "customer_ids" | "sources">,
  monitored: readonly { customer_id: string; source: string }[],
  max = 3,
): string[] {
  if (agent.scope_type === "customers") {
    const selected = agent.customer_ids ?? [];
    const withSource = new Set(monitored.map((m) => m.customer_id));
    return [...selected.filter((id) => withSource.has(id)), ...selected.filter((id) => !withSource.has(id))].slice(0, max);
  }
  const matching = monitored.filter((m) => (agent.sources as string[]).includes(m.source)).map((m) => m.customer_id);
  const others = monitored.map((m) => m.customer_id);
  return [...new Set([...matching, ...others])].slice(0, max);
}
